import type { LayoutData, Shape } from '@eventflow/shared-types';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

type Tool = 'select' | 'circle' | 'polygon' | 'rect' | 'overlay-rect' | 'overlay-circle';

export interface MapEditorState {
  shapes: Shape[];
  selectedShapeId: string | null;
  selectedShapeIds: string[];
  tool: Tool;
  backgroundImage: string | null;
  dimensions: { width: number; height: number };
  zoom: number;
  isDirty: boolean;
  history: LayoutData[];
  historyIndex: number;
  clipboard: Shape | null;
}

const initialState: MapEditorState = {
  shapes: [],
  selectedShapeId: null,
  selectedShapeIds: [],
  tool: 'select',
  backgroundImage: null,
  dimensions: { width: 1200, height: 800 },
  zoom: 1,
  isDirty: false,
  history: [],
  historyIndex: -1,
  clipboard: null,
};

const mapEditorSlice = createSlice({
  name: 'mapEditor',
  initialState,
  reducers: {
    setLayoutData: (state, action: PayloadAction<LayoutData>) => {
      state.shapes = action.payload.shapes;
      state.backgroundImage = action.payload.backgroundImage || null;
      state.dimensions = action.payload.dimensions;
      state.isDirty = false;
      // Inicializar historial
      state.history = [action.payload];
      state.historyIndex = 0;
    },

    addShape: (state, action: PayloadAction<Shape>) => {
      // Asegurar que shapes esté inicializado
      if (!state.shapes) {
        state.shapes = [];
      }

      state.shapes.push(action.payload);
      state.isDirty = true;
      state.selectedShapeId = action.payload.id;
      state.selectedShapeIds = [action.payload.id];

      // Agregar al historial
      mapEditorSlice.caseReducers.pushHistory(state);
    },

    updateShape: (state, action: PayloadAction<Shape>) => {
      const index = state.shapes.findIndex((s) => s.id === action.payload.id);
      if (index !== -1) {
        state.shapes[index] = action.payload;
        state.isDirty = true;
      }
    },

    updateShapesBatch: (
      state,
      action: PayloadAction<{ ids: string[]; updates: Partial<Shape> }>
    ) => {
      const { ids, updates } = action.payload;
      state.shapes = state.shapes.map((s) => (ids.includes(s.id) ? { ...s, ...updates } : s));
      state.isDirty = true;
    },

    deleteShape: (state, action: PayloadAction<string>) => {
      state.shapes = state.shapes.filter((s) => s.id !== action.payload);
      if (state.selectedShapeId === action.payload) {
        state.selectedShapeId = null;
      }
      state.selectedShapeIds = state.selectedShapeIds.filter((id) => id !== action.payload);
      state.isDirty = true;
      mapEditorSlice.caseReducers.pushHistory(state);
    },

    deleteShapes: (state, action: PayloadAction<string[]>) => {
      const ids = action.payload;
      state.shapes = state.shapes.filter((s) => !ids.includes(s.id));
      if (state.selectedShapeId && ids.includes(state.selectedShapeId)) {
        state.selectedShapeId = null;
      }
      state.selectedShapeIds = state.selectedShapeIds.filter((id) => !ids.includes(id));
      state.isDirty = true;
      mapEditorSlice.caseReducers.pushHistory(state);
    },

    selectShape: (state, action: PayloadAction<string | null>) => {
      state.selectedShapeId = action.payload;
      state.selectedShapeIds = action.payload ? [action.payload] : [];
    },

    toggleSelectShape: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedShapeIds.includes(id)) {
        state.selectedShapeIds = state.selectedShapeIds.filter((s) => s !== id);
      } else {
        state.selectedShapeIds = [...state.selectedShapeIds, id];
      }
      state.selectedShapeId = state.selectedShapeIds[0] || null;
    },

    clearSelection: (state) => {
      state.selectedShapeId = null;
      state.selectedShapeIds = [];
    },

    setTool: (state, action: PayloadAction<Tool>) => {
      state.tool = action.payload;
      if (action.payload !== 'select') {
        state.selectedShapeId = null;
        state.selectedShapeIds = [];
      }
    },

    setBackgroundImage: (state, action: PayloadAction<string>) => {
      state.backgroundImage = action.payload;
      state.isDirty = true;
    },

    setDimensions: (state, action: PayloadAction<{ width: number; height: number }>) => {
      state.dimensions = action.payload;
      state.isDirty = true;
    },

    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = Math.max(0.1, Math.min(3, action.payload));
    },

    markAsSaved: (state) => {
      state.isDirty = false;
    },

    pushHistory: (state) => {
      const newState: LayoutData = {
        shapes: state.shapes,
        backgroundImage: state.backgroundImage || undefined,
        dimensions: state.dimensions,
      };

      // Eliminar estados futuros si estamos en medio del historial
      state.history = state.history.slice(0, state.historyIndex + 1);
      state.history.push(newState);
      state.historyIndex += 1;

      // Limitar historial a 50 estados
      if (state.history.length > 50) {
        state.history.shift();
        state.historyIndex -= 1;
      }
    },

    undo: (state) => {
      if (state.historyIndex > 0) {
        state.historyIndex -= 1;
        const prevState = state.history[state.historyIndex];
        state.shapes = prevState.shapes;
        state.backgroundImage = prevState.backgroundImage || null;
        state.dimensions = prevState.dimensions;
        state.isDirty = true;
      }
    },

    redo: (state) => {
      if (state.historyIndex < state.history.length - 1) {
        state.historyIndex += 1;
        const nextState = state.history[state.historyIndex];
        state.shapes = nextState.shapes;
        state.backgroundImage = nextState.backgroundImage || null;
        state.dimensions = nextState.dimensions;
        state.isDirty = true;
      }
    },

    copyShape: (state) => {
      if (state.selectedShapeId) {
        const shapeToCopy = state.shapes.find((s) => s.id === state.selectedShapeId);
        if (shapeToCopy) {
          state.clipboard = shapeToCopy;
        }
      }
    },

    pasteShape: (state) => {
      if (state.clipboard) {
        // Si es overlay, duplicar sin generar IDs de mesa
        if ((state.clipboard as any).category === 'overlay') {
          const newOverlay = {
            ...(state.clipboard as any),
            id: `shape-${Date.now()}`,
            x: (state.clipboard.x || 0) + 20,
            y: (state.clipboard.y || 0) + 20,
          } as any as Shape;
          state.shapes.push(newOverlay as Shape);
          state.selectedShapeId = (newOverlay as Shape).id;
          state.isDirty = true;
          mapEditorSlice.caseReducers.pushHistory(state);
          return;
        }

        // Caso normal: mesa. Determinar el siguiente TABLE-{N} disponible basado en tableId/seatIdentifier existentes
        const tableNumbers = state.shapes
          .map((s) => {
            const id = (s as any).tableId || s.seatIdentifier || '';
            const match = id.match(/^TABLE-(\d+)$/i);
            return match ? Number.parseInt(match[1], 10) : 0;
          })
          .filter((n) => n > 0);

        const maxTableNumber = tableNumbers.length > 0 ? Math.max(...tableNumbers) : 0;
        const nextNumber = maxTableNumber + 1;
        const nextTableId = `TABLE-${nextNumber}`;

        const newShape = {
          ...(state.clipboard as any),
          id: `shape-${Date.now()}`,
          // Autogenerar identificadores de mesa
          tableId: nextTableId,
          seatIdentifier: nextTableId,
          tableLabel: `Mesa ${nextNumber}`,
          // Desplazar ligeramente para que sea visible
          x: (state.clipboard.x || 0) + 20,
          y: (state.clipboard.y || 0) + 20,
          hidden: (state.clipboard as any).hidden ?? false,
          category: (state.clipboard as any).category || 'table',
        } as any as Shape;
        state.shapes.push(newShape as Shape);
        state.selectedShapeId = (newShape as Shape).id;
        state.isDirty = true;
        mapEditorSlice.caseReducers.pushHistory(state);
      }
    },

    reset: () => initialState,
  },
});

export const {
  setLayoutData,
  addShape,
  updateShape,
  updateShapesBatch,
  deleteShape,
  deleteShapes,
  selectShape,
  toggleSelectShape,
  clearSelection,
  setTool,
  setBackgroundImage,
  setDimensions,
  setZoom,
  markAsSaved,
  pushHistory,
  undo,
  redo,
  copyShape,
  pasteShape,
  reset,
} = mapEditorSlice.actions;

export default mapEditorSlice.reducer;
